import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Play, Pause, X, Maximize2, Volume2, VolumeX, Clock, Lock
} from 'lucide-react';
import { useVideo } from '../context/VideoContext';

export const VideoMiniPlayer: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch, videoRef, closeVideo } = useVideo();

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !state.isMiniPlayer) return;

    const handleTimeUpdate = () => {
      dispatch({ type: 'SET_CURRENT_TIME', payload: videoEl.currentTime });

      if (state.playbackMode === 'trailer') {
        const remaining = Math.max(0, videoEl.duration - videoEl.currentTime);
        dispatch({ type: 'SET_TRAILER_TIME_REMAINING', payload: remaining });
      }

      if (state.playbackMode === 'preview' && !state.hasUnlimited && !state.currentVideo?.isPurchased) {
        const remaining = Math.max(0, 30 - videoEl.currentTime);
        dispatch({ type: 'SET_PREVIEW_TIME_REMAINING', payload: remaining });

        if (videoEl.currentTime >= 30) {
          videoEl.pause();
          dispatch({ type: 'SHOW_PURCHASE_PROMPT', payload: true });
        }
      }
    };

    const handleEnded = () => {
      if (state.playbackMode === 'trailer') {
        if (state.hasUnlimited || state.currentVideo?.isPurchased) {
          dispatch({ type: 'SET_PLAYBACK_MODE', payload: 'full' });
        } else {
          dispatch({ type: 'SHOW_PURCHASE_PROMPT', payload: true });
        }
      }
    };

    videoEl.addEventListener('timeupdate', handleTimeUpdate);
    videoEl.addEventListener('ended', handleEnded);

    return () => {
      videoEl.removeEventListener('timeupdate', handleTimeUpdate);
      videoEl.removeEventListener('ended', handleEnded);
    };
  }, [state.isMiniPlayer, state.playbackMode, state.hasUnlimited, state.currentVideo, dispatch, videoRef]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (state.isPlaying) {
        videoRef.current.pause();
        dispatch({ type: 'PAUSE' });
      } else {
        videoRef.current.play();
        dispatch({ type: 'PLAY' });
      }
    }
  };

  const handleExpand = () => {
    dispatch({ type: 'SET_MINI_PLAYER', payload: false });
    if (state.currentVideo) {
      navigate(`/video/${state.currentVideo.id}`);
    }
  };

  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    closeVideo();
  };

  const toggleMute = () => {
    const newVol = state.volume === 0 ? 1 : 0;
    dispatch({ type: 'SET_VOLUME', payload: newVol });
    if (videoRef.current) {
      videoRef.current.volume = newVol;
    }
  };

  const formatTime = (time: number) => {
    if (isNaN(time) || !isFinite(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!state.currentVideo || !state.isMiniPlayer) return null;

  const isTrailerMode = state.playbackMode === 'trailer';
  const isPreviewMode = state.playbackMode === 'preview';
  const canAccessFull = state.hasUnlimited || state.currentVideo.isPurchased;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-50 glass-royal backdrop-blur-royal border-t border-royal-border"
      >
        <div
          className="h-1 bg-royal-border cursor-pointer"
          onClick={(e) => {
            if (!videoRef.current || !state.duration) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const percent = (e.clientX - rect.left) / rect.width;
            const newTime = percent * state.duration;
            if (isPreviewMode && !canAccessFull && newTime > 30) return;
            videoRef.current.currentTime = newTime;
          }}
        >
          <div
            className="h-full bg-royal-gold transition-all"
            style={{ width: `${(state.currentTime / state.duration) * 100 || 0}%` }}
          />
        </div>

        <div className="px-4 py-3">
          <div className="max-w-screen-2xl mx-auto flex items-center gap-4">
            <div
              className="relative w-24 h-14 bg-black rounded-lg overflow-hidden cursor-pointer flex-shrink-0 group"
              onClick={handleExpand}
            >
              <video
                ref={videoRef}
                src={
                  isTrailerMode && state.currentVideo.trailerUrl
                    ? state.currentVideo.trailerUrl
                    : state.currentVideo.videoUrl
                }
                className="w-full h-full object-cover"
                muted={false}
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Maximize2 className="w-5 h-5 text-white" />
              </div>

              {(isTrailerMode || (isPreviewMode && !canAccessFull)) && (
                <div className="absolute top-1 left-1">
                  <div className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                    isTrailerMode ? 'bg-red-600 text-white' : 'bg-royal-gold text-gray-900'
                  }`}>
                    {isTrailerMode ? 'TRAILER' : 'PREVIEW'}
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-royal-text truncate">
                  {state.currentVideo.title}
                </h4>
                {(isTrailerMode || (isPreviewMode && !canAccessFull)) && (
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-royal-border rounded-full text-xs text-royal-text-secondary flex-shrink-0">
                    <Clock className="w-3 h-3" />
                    {isTrailerMode
                      ? formatTime(state.trailerTimeRemaining)
                      : `${Math.ceil(state.previewTimeRemaining)}s`}
                  </div>
                )}
              </div>
              <p className="text-xs text-royal-text-secondary truncate">
                {state.currentVideo.artist}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={toggleMute}
                className="p-2 hover:bg-royal-hover rounded-full transition-colors"
              >
                {state.volume === 0 ? (
                  <VolumeX className="w-5 h-5 text-royal-text-secondary" />
                ) : (
                  <Volume2 className="w-5 h-5 text-royal-text-secondary" />
                )}
              </button>

              <button
                onClick={handlePlayPause}
                className="p-3 bg-royal-gold text-gray-900 rounded-full hover:bg-royal-gold-dark transition-colors"
              >
                {state.isPlaying ? (
                  <Pause className="w-5 h-5" fill="currentColor" />
                ) : (
                  <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                )}
              </button>

              <button
                onClick={handleExpand}
                className="p-2 hover:bg-royal-hover rounded-full transition-colors"
              >
                <Maximize2 className="w-5 h-5 text-royal-text-secondary" />
              </button>

              <button
                onClick={handleClose}
                className="p-2 hover:bg-royal-hover rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-royal-text-secondary" />
              </button>
            </div>
          </div>
        </div>

        {state.showPurchasePrompt && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute bottom-full left-0 right-0 p-4 bg-gray-900 border-t border-royal-border"
          >
            <div className="max-w-screen-2xl mx-auto flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-royal-gold/20 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-royal-gold" />
                </div>
                <div>
                  <p className="text-white font-medium">
                    {isTrailerMode ? 'Ready to watch the full video?' : 'Preview ended'}
                  </p>
                  <p className="text-sm text-gray-400">
                    Purchase or subscribe to continue watching
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-4 py-2 bg-royal-gold text-gray-900 rounded-xl font-medium hover:bg-royal-gold-dark transition-colors"
                >
                  Get Unlimited
                </button>
                <button
                  onClick={() => {
                    dispatch({ type: 'SHOW_PURCHASE_PROMPT', payload: false });
                    navigate(`/video/${state.currentVideo?.id}`);
                  }}
                  className="px-4 py-2 bg-white text-gray-900 rounded-xl font-medium hover:bg-gray-100 transition-colors"
                >
                  Buy ${state.currentVideo?.price.toFixed(2)}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default VideoMiniPlayer;
